import { useContext, useEffect, useState } from 'react';

import {
	ArrowLeftOutlined,
	BookOutlined,
	DeleteOutlined,
	EditOutlined,
	ReloadOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Flex,
	Input,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tooltip,
	message,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import {
	useDeleteSingleMonitor,
	useGetContentMonitors,
	useGetSingleMonitorById,
	useGetSingleMonitorCategoriesById,
} from '@/api/monitor_collections';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import { PageContext } from '@/providers/PageContextProvider';

import { IPagination } from '../../video-intelligence-on-demand';
import SingleMonitorCategoriesTable from './SingleMonitorCategoriesTable';
import { singleMonitorCategoriesColumns } from './data';

export default function SingleContentMonitor() {
	const navigate = useNavigate();
	const { id = '' } = useParams<{ id: string }>();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const { setPageTitle, openDrawer, setIsCopilotCreditsCounterVisible } =
		useContext(PageContext);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});

	const [search, setSearch] = useState('');
	const [sortBy, setSortBy] = useState<'created_at'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const debouncedSearch = useDebounce(search);

	const {
		data: monitorData,
		isLoading: isMonitorLoading,
		isFetching: isMonitorFetching,
	} = useGetSingleMonitorById({
		id: id ? Number(id) : null,
		enabled: id ? !!Number(id) : false,
	});
	const { mutate: deleteMonitor, isLoading: isDeleteMonitorLoading } =
		useDeleteSingleMonitor();

	const monitor_id = monitorData?.data?.data?.item?.id;

	const {
		data: singleMonitorCategoriesData,
		isLoading,
		isFetching,
		refetch,
	} = useGetSingleMonitorCategoriesById({
		id: id ? Number(id) : null,
		enabled: id ? !!Number(id) : false,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
		page: pagination.page,
		limit: pagination.limit,
		sortBy,
		sort: sortOrder,
	});

	const total = singleMonitorCategoriesData?.data?.data?.total ?? 1;
	const categoriesItems = singleMonitorCategoriesData?.data?.data?.items;

	useEffect(() => {
		setPageTitle('Brand Alert');
		setIsCopilotCreditsCounterVisible(true);

		return () => {
			setPageTitle('');
			setIsCopilotCreditsCounterVisible(false);
		};
	}, []);

	useEffect(() => {
		refetch();
	}, [sortOrder, pagination]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.copilot)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'created_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortBy', field);
			// 	return searchParams;
			// });
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortOrder', order === 'ascend' ? 'asc' : 'desc');
			// 	return searchParams;
			// });
		}
	};

	const onAddCategoryClick = () => {
		openDrawer('add-new-category', {
			id: id,
		});
	};

	const onEditClick = () => {
		openDrawer('edit-monitor', {
			id: id,
		});
	};

	const onDeleteClick = (id: number) => {
		deleteMonitor(id, {
			onSuccess: () => {
				message.success('Removed');
				navigate('/app/dashboard/content-monitor');
				// setSearch("");
			},
			onError: (error) => {
				const err = error as any;
				console.error('Error deleting collection:', err.response.data.message);
				message.error(err.response.data.message);
			},
		});
	};

	return (
		<Spin
			spinning={
				isLoading || isFetching || isMonitorLoading || isMonitorFetching
			}
		>
			<div>
				<Flex
					align="center"
					justify="space-between"
					style={{ marginBottom: '1.5rem' }}
				>
					<h2>Monitor: {monitorData?.data?.data?.item?.title}</h2>

					<Space>
						<Tooltip title="Refresh" placement="left">
							<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
						</Tooltip>
						<Tooltip title="Back to monitors" placement="left">
							<Button
								onClick={() => navigate('/app/dashboard/content-monitor')}
								icon={<ArrowLeftOutlined />}
							/>
						</Tooltip>
					</Space>
				</Flex>

				<Flex vertical gap={16} style={{ width: '100%' }}>
					<Flex
						gap={8}
						justify="space-between"
						align="center"
						style={{ width: '100%' }}
					>
						<Input
							placeholder="Search categories"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							style={{ width: '300px' }}
						/>
						<Flex gap={16}>
							<Button type="primary" onClick={onAddCategoryClick}>
								Add category
							</Button>
							<Tooltip title="Edit monitor" placement="top">
								<Button onClick={onEditClick}>
									<EditOutlined />
								</Button>
							</Tooltip>

							<Tooltip title="Delete monitor" placement="topLeft">
								<Popconfirm
									title="Sure to remove monitor?"
									onConfirm={() => onDeleteClick(Number(id))}
								>
									<Button
										danger
										disabled={isDeleteMonitorLoading}
										loading={isDeleteMonitorLoading}
									>
										<DeleteOutlined />
									</Button>
								</Popconfirm>
							</Tooltip>
						</Flex>
					</Flex>
					<SingleMonitorCategoriesTable
						data={categoriesItems || []}
						columns={singleMonitorCategoriesColumns}
						onTableChange={onTableChange}
						isLoading={isLoading}
						isFetching={isFetching}
						pagination={{ ...pagination, total }}
						setPagination={setPagination}
						onEditCreatorClick={() => {}}
						monitor_id={monitor_id}
					/>
					{/* <Table
						loading={isLoading || isFetching}
						// style={{ width: '100%' }}
						onChange={onTableChange}
						dataSource={items}
						columns={[
							...columns,
							{
								title: 'Actions',
								dataIndex: 'actions',
								key: 'actions',
								width: 120,
								ellipsis: false,
								fixed: 'right',
								// flex: true,
								render: (_: any, record: any) => {
									return (
										<Flex justify="center" align="center" gap={8}>
											<Tooltip title="View">
												<Button size="small">
													<UnorderedListOutlined />
												</Button>
											</Tooltip>
											<Tooltip title="Edit">
												<Button size="small">
													<EditOutlined />
												</Button>
											</Tooltip>
											<Tooltip title="Delete">
												<Button danger size="small">
													<DeleteOutlined />
												</Button>
											</Tooltip>
										</Flex>
									);
								},
							},
						]}
						rowKey={'id'}
						bordered
						scroll={{ x: 1100 }}
						pagination={{
							total,
							showSizeChanger: true,
							current: pagination.page,
							pageSizeOptions: [10, 25, 50],
							onChange: (page: number, pageSize: number) => {
								setPagination((cur) => ({ ...cur, page, limit: pageSize }));
							},
						}}
					/> */}
				</Flex>
			</div>
		</Spin>
	);
}
